import { Plus, ArrowUpRight } from "lucide-react";
import { productCategoryIcons } from "./productCategoryIcons";
import type { Locale } from "../i18n";
import { SpotlightCard } from "../components/react-bits/SpotlightCard";
import { formatStorePrice, type StoreCatalog } from "../core/storeCatalog";
import { engagementContent } from "./engagementContent";
import { socialContent } from "./socialContent";
import type { DiscoveryAccount } from "./useDiscoveryAccount";

interface Props {
  readonly item: StoreCatalog["products"][number];
  readonly catalog: StoreCatalog;
  readonly locale: Locale;
  readonly account: DiscoveryAccount;
  readonly signedIn: boolean;
  readonly selected: boolean;
  readonly compareDisabled: boolean;
  readonly detail: boolean;
  readonly login: string;
  readonly onCompare: () => void;
  readonly onAdd: (id: string) => Promise<void>;
}

export function ProductCard({ item, catalog, locale, account, signedIn, selected, compareDisabled, detail, login, onCompare, onAdd }: Props): React.JSX.Element {
  const t = engagementContent(locale), social = socialContent(locale);
  const Icon = productCategoryIcons[item.category];
  const href = `/shop?lang=${locale}&product=${item.id}`;
  const reviews = catalog.reviews.filter(review => review.product_id === item.id);
  return <SpotlightCard className={`dc-product-card dc-product-${item.category}`}>
    <article>
      <div className="dc-product-visual">
        <a className="dc-product-symbol" href={href} aria-label={item.name}><Icon size={78} strokeWidth={.9} aria-hidden="true" /></a>
        <span className="dc-product-category">{t.categories[item.category]}</span>
        <label className="dc-product-compare"><input type="checkbox" aria-label={`${social.compare}: ${item.name}`} checked={selected} disabled={compareDisabled} onChange={onCompare} /><span>{social.compare}</span></label>
      </div>
      <div className="dc-product-copy">
        <h2><a href={href}>{item.name}<ArrowUpRight size={18} aria-hidden="true" /></a></h2>
        <p className="dc-preserve">{item.description}</p>
        {catalog.variants.filter(variant => variant.product_id === item.id).map(variant => {
          const unavailable = variant.stocked_quantity - variant.reserved_quantity < 1 || variant.currency_code !== "THB";
          const disabled = account.snapshot === null || account.busy || (account.snapshot.cart.find(line => line.variantId === variant.id)?.quantity ?? 0) >= 99 || unavailable;
          return <div className="dc-store-variant" key={variant.id}>
            <div><b>{variant.title}</b><p>{formatStorePrice(variant.price_minor, variant.currency_code, locale)}</p></div>
            {signedIn ? <button className="dc-button dc-outline" disabled={disabled} onClick={() => { void onAdd(variant.id); }}><Plus size={15} aria-hidden="true" />{account.busy ? t.pending : unavailable ? t.soldOut : t.add}</button> : <a className="dc-button dc-outline" href={login}>{t.login}</a>}
          </div>;
        })}
        <p className="dc-product-review">{reviews.length === 0 ? t.newProduct : `${t.reviews} (${String(reviews.length)})`}</p>
        {detail ? reviews.map(review => <blockquote key={review.id}><b>{review.rating}/5</b><p>{review.body}</p></blockquote>) : null}
      </div>
    </article>
  </SpotlightCard>;
}
